import { useInfiniteQuery, useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { Eye, Pencil, Trash2, Search, MessageSquare, Printer, Plus, Truck, CalendarDays } from "lucide-react";
import type { OrderWithItems } from "@shared/schema";
import EditOrderDialog from "@/components/admin/edit-order-dialog";
import AddOrderDialog from "@/components/admin/add-order-dialog";
import { useCompanyLogo } from "@/hooks/use-company-logo";

export default function AdminOrdersPage() {
  const { toast } = useToast();
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);
  const [editingOrder, setEditingOrder] = useState<OrderWithItems | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const logoUrl = useCompanyLogo();

  const PAGE_LIMIT = 20;

  const {
    data: ordersData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{ orders: OrderWithItems[]; total: number; page: number; limit: number }>({
    queryKey: ["/api/admin/orders", "paginated"],
    queryFn: ({ pageParam }) =>
      apiRequest("GET", `/api/admin/orders?page=${pageParam}&limit=${PAGE_LIMIT}`).then((r) => r.json()),
    getNextPageParam: (lastPage) =>
      lastPage.page * lastPage.limit < lastPage.total ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  });

  const orders = ordersData?.pages.flatMap((p) => p.orders) ?? [];
  const totalOrders = ordersData?.pages[0]?.total ?? 0;

  const { data: invoicedOrderIds } = useQuery<string[]>({
    queryKey: ["/api/admin/invoiced-order-ids"],
  });

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  const invoicedSet = new Set(invoicedOrderIds || []);

  const changeStatusMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: string }) =>
      apiRequest("PATCH", `/api/admin/orders/${orderId}/status`, { status }),
    onSuccess: () => {
      toast({ title: "Status uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
    },
    onError: () => {
      toast({
        title: "Fel",
        description: "Kunde inte uppdatera status.",
        variant: "destructive",
      });
    },
  });

  const deleteOrderMutation = useMutation({
    mutationFn: (orderId: string) => apiRequest("DELETE", `/api/admin/orders/${orderId}`),
    onSuccess: () => {
      toast({ title: "Order raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      setDeletingOrderId(null);
    },
    onError: () => {
      toast({
        title: "Fel",
        description: "Kunde inte radera order.",
        variant: "destructive",
      });
    },
  });

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    return orders.filter((order) => {
      const matchesSearch = 
        order.client?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || order.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, searchQuery, statusFilter]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredOrders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const selectedOrders = orders?.filter(o => selectedIds.has(o.id)) || [];
  const [printMode, setPrintMode] = useState<"delivery" | "picklist">("delivery");

  const handlePrintSelected = () => {
    setPrintMode("delivery");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const handlePrintPicklist = () => {
    setPrintMode("picklist");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const aggregatedProducts = useMemo(() => {
    if (selectedOrders.length === 0) return [];
    const productMap = new Map<string, { name: string; unit: string; totalQuantity: number }>();
    for (const order of selectedOrders) {
      for (const item of order.items || []) {
        const productId = item.productId;
        const existing = productMap.get(productId);
        if (existing) {
          existing.totalQuantity += item.quantity;
        } else {
          productMap.set(productId, {
            name: item.product?.name || "Okänd produkt",
            unit: item.product?.sellUnit || "pcs",
            totalQuantity: item.quantity,
          });
        }
      }
    }
    return Array.from(productMap.values()).sort((a, b) => a.name.localeCompare(b.name, "sv"));
  }, [selectedOrders]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Väntande", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      confirmed: { label: "Bekräftad", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
      in_production: { label: "I produktion", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
      packed: { label: "Färdig att leverera", className: "bg-teal-500/10 text-teal-600 border-teal-500/20" },
      delivered: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      levererad: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
    };
    const variant = variants[status] || { label: status, className: "" };
    return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Orderhistorik</h1>
          <p className="text-muted-foreground">Alla ordrar i systemet</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <>
              <Button variant="outline" onClick={handlePrintPicklist} data-testid="button-print-picklist">
                <Truck className="h-4 w-4 mr-2" />
                Bil Plocklista ({selectedIds.size})
              </Button>
              <Button onClick={handlePrintSelected} data-testid="button-print-selected">
                <Printer className="h-4 w-4 mr-2" />
                Följesedlar ({selectedIds.size})
              </Button>
            </>
          )}
          <Button onClick={() => setAddOrderOpen(true)} data-testid="button-add-order">
            <Plus className="h-4 w-4 mr-2" />
            Ny order
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Ordrar</CardTitle>
              <CardDescription>
                Visar {orders.length} av {totalOrders} ordrar
                {(searchQuery || statusFilter !== "all") && ` (${filteredOrders.length} matchade)`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Sök kund eller order..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="input-search-orders"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="select-status-filter">
                  <SelectValue placeholder="Alla statusar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla statusar</SelectItem>
                  <SelectItem value="pending">Väntande</SelectItem>
                  <SelectItem value="confirmed">Bekräftad</SelectItem>
                  <SelectItem value="in_production">I produktion</SelectItem>
                  <SelectItem value="packed">Färdig att leverera</SelectItem>
                  <SelectItem value="delivered">Levererad</SelectItem>
                  <SelectItem value="levererad">Levererad</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    <TableHead>Order</TableHead>
                    <TableHead>Kund</TableHead>
                    <TableHead>Belopp</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trågare</TableHead>
                    <TableHead>Bagare</TableHead>
                    <TableHead>Packad av</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-28 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <Skeleton className="h-8 w-8 rounded-md" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={filteredOrders.length > 0 && selectedIds.size === filteredOrders.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="checkbox-select-all"
                    />
                  </TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Kund</TableHead>
                  <TableHead>Belopp</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trågare</TableHead>
                  <TableHead>Bagare</TableHead>
                  <TableHead>Packad av</TableHead>
                  <TableHead className="text-right">Åtgärd</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} className={selectedIds.has(order.id) ? "bg-primary/5" : ""}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(order.id)}
                        onCheckedChange={() => toggleSelect(order.id)}
                        data-testid={`checkbox-order-${order.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <span className="flex items-center gap-1 flex-wrap">
                        #{order.orderNumber}
                        {order.notes && <MessageSquare className="h-3 w-3 text-muted-foreground" />}
                        {order.scheduledDelivery && (
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-xs font-normal" data-testid={`badge-scheduled-${order.id}`}>
                            <CalendarDays className="h-3 w-3 mr-1" />
                            Framtida
                          </Badge>
                        )}
                        {order.seller && (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/20 text-xs font-normal" data-testid={`badge-seller-${order.id}`}>
                            Säljare: {order.seller.name}
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {order.client?.name || "Okänd kund"}
                    </TableCell>
                    <TableCell>{order.totalAmount} kr</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm">{format(new Date(order.orderDate!), "d MMM yyyy HH:mm", { locale: sv })}</span>
                        {order.scheduledDelivery && order.deliveryDate && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-0.5">
                            <CalendarDays className="h-3 w-3" />
                            Leverans: {format(new Date(order.deliveryDate), "d MMM yyyy", { locale: sv })}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Select
                          value={order.status}
                          onValueChange={(newStatus) => {
                            if (newStatus !== order.status) {
                              changeStatusMutation.mutate({ orderId: order.id, status: newStatus });
                            }
                          }}
                        >
                          <SelectTrigger className="h-7 w-auto min-w-[140px] text-xs border-0 p-0 shadow-none focus:ring-0" data-testid={`select-status-${order.id}`}>
                            <SelectValue>{getStatusBadge(order.status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Väntande</SelectItem>
                            <SelectItem value="confirmed">Bekräftad</SelectItem>
                            <SelectItem value="in_production">I produktion</SelectItem>
                            <SelectItem value="packed">Färdig att leverera</SelectItem>
                            <SelectItem value="delivered">Levererad</SelectItem>
                          </SelectContent>
                        </Select>
                        {invoicedSet.has(order.id) && (
                          <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20" data-testid={`badge-invoiced-${order.id}`}>
                            Fakturerad
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{order.tragareBy || "-"}</TableCell>
                    <TableCell>{order.bagareBy || "-"}</TableCell>
                    <TableCell>{order.packedBy || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setSelectedOrder(order)}
                          data-testid={`button-view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditingOrder(order)}
                          data-testid={`button-edit-order-${order.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog open={deletingOrderId === order.id} onOpenChange={(open) => !open && setDeletingOrderId(null)}>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setDeletingOrderId(order.id)}
                              data-testid={`button-delete-order-${order.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Radera order?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Detta kommer att permanent radera ordern för {order.client?.name}. 
                                Denna åtgärd kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteOrderMutation.mutate(order.id)}
                                disabled={deleteOrderMutation.isPending}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {deleteOrderMutation.isPending ? "Raderar..." : "Radera"}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              {searchQuery || statusFilter !== "all" ? "Inga ordrar matchade sökningen" : "Inga ordrar ännu"}
            </div>
          )}
          {(hasNextPage || isFetchingNextPage) && (
            <div className="pt-4 flex flex-col items-center gap-2">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                data-testid="button-load-more-orders"
              >
                {isFetchingNextPage ? "Laddar..." : `Ladda fler (${orders.length} av ${totalOrders})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedOrders.length > 0 && (
        <div className={`print-delivery-notes ${printMode === "picklist" ? "print-active" : ""}`}>
          <div className="delivery-note-page a4-page bg-white text-black">
            <div className="p-6 space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-black">Bil Plocklista</h1>
                  <p className="text-xs text-gray-500">{format(new Date(), "d MMM yyyy", { locale: sv })} — {selectedOrders.length} ordrar</p>
                </div>
                <img src={logoUrl} alt="Logo" className="h-10 w-auto max-w-[80px] object-contain" />
              </div>

              <div className="h-0.5 bg-black w-full" />

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-300">
                    <th className="py-1 font-semibold text-black">Produkt</th>
                    <th className="py-1 font-semibold text-right text-black">Antal</th>
                    <th className="py-1 font-semibold text-right text-black">Enhet</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregatedProducts.map((product, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-1 text-black">{product.name}</td>
                      <td className="py-1 text-right text-black font-bold text-base">{product.totalQuantity}</td>
                      <td className="py-1 text-right text-gray-600">{getUnitLabel(product.unit)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-black font-bold">
                    <td className="py-1 text-black">Totalt</td>
                    <td className="py-1 text-right text-black text-base">{aggregatedProducts.reduce((sum, p) => sum + p.totalQuantity, 0)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              <div className="pt-2">
                <p className="text-xs font-semibold text-black mb-1">Per kund:</p>
                {selectedOrders.map((order) => (
                  <div key={order.id} className="mb-1 text-xs">
                    <span className="font-medium text-black">#{order.orderNumber} {order.client?.name}</span>
                    {order.client?.address && <span className="text-gray-400"> — {order.client.address}</span>}
                    <span className="text-gray-500 ml-1">
                      ({order.items?.map(i => `${i.product?.name} ×${i.quantity}`).join(", ")})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedOrders.length > 0 && (
        <div className={`print-delivery-notes ${printMode === "delivery" ? "print-active" : ""}`}>
          {selectedOrders.map((order) => (
            <div key={order.id} className="delivery-note-page a4-page bg-white text-black">
              <div className="p-8 space-y-6">
                <div className="flex justify-between items-start">
                  <h1 className="text-4xl font-bold italic text-black">Följesedel</h1>
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="h-16 w-auto max-w-[120px] object-contain"
                  />
                </div>

                <div className="h-1 bg-black w-full" />

                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    {settings?.companyName && (
                      <h2 className="font-bold text-lg mb-2 text-black">{settings.companyName}</h2>
                    )}
                    {settings?.companyAddress && (
                      <p className="text-gray-600 italic">{settings.companyAddress}</p>
                    )}
                    {settings?.companyPhone && (
                      <p className="text-gray-600 italic">Telefon: {settings.companyPhone}</p>
                    )}
                    {settings?.companyEmail && (
                      <p className="text-gray-600 italic">{settings.companyEmail}</p>
                    )}
                  </div>
                  <div>
                    <h2 className="font-bold text-lg mb-2 text-black">Mottagare</h2>
                    <p className="text-gray-600 italic">{order.client?.name}</p>
                    {order.client?.address && (
                      <p className="text-gray-600 italic">{order.client.address}</p>
                    )}
                    {order.client?.phone && (
                      <p className="text-gray-600 italic">Tel: {order.client.phone}</p>
                    )}
                    {order.client?.email && (
                      <p className="text-gray-600 italic">{order.client.email}</p>
                    )}
                  </div>
                </div>

                <div className="pt-8">
                  <div className="h-px bg-black w-full mb-4" />
                  <table className="w-full">
                    <thead>
                      <tr className="text-left">
                        <th className="py-2 font-semibold text-black">Beskrivning</th>
                        <th className="py-2 font-semibold text-black">Datum</th>
                        <th className="py-2 font-semibold text-right text-black">Antal</th>
                        <th className="py-2 font-semibold text-right text-black">Enhet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items?.map((item) => (
                        <tr key={item.id} className="border-t border-gray-200">
                          <td className="py-3 text-black">{item.product?.name}</td>
                          <td className="py-3 text-gray-600">
                            {format(new Date(order.orderDate!), "yyyy-MM-dd", { locale: sv })}
                          </td>
                          <td className="py-3 text-right text-black">{item.quantity}</td>
                          <td className="py-3 text-right text-black">{getUnitLabel(item.product?.sellUnit || "pcs")}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 font-semibold">
                        <td className="py-3 text-black">Totalt</td>
                        <td></td>
                        <td className="py-3 text-right text-black">{order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {(order.client?.hasRut && order.client.rutDetails) || order.client?.notes ? (
                  <div className="pt-4 grid grid-cols-2 gap-8">
                    <div>
                      {order.client?.hasRut && order.client.rutDetails && (
                        <>
                          <h3 className="font-semibold mb-2 text-black">Leveransrutt</h3>
                          <p className="text-gray-600 italic">{order.client.rutDetails}</p>
                        </>
                      )}
                    </div>
                    <div>
                      {order.client?.notes && (
                        <>
                          <h3 className="font-semibold mb-2 text-black">Anteckningar</h3>
                          <p className="text-gray-600 italic">{order.client.notes}</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <EditOrderDialog 
        order={editingOrder} 
        open={!!editingOrder} 
        onOpenChange={(open) => !open && setEditingOrder(null)} 
      />

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Order #{selectedOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {selectedOrder.scheduledDelivery && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <CalendarDays className="h-4 w-4 text-blue-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Framtida order</p>
                    {selectedOrder.deliveryDate && (
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        Planerad leverans: {format(new Date(selectedOrder.deliveryDate), "d MMMM yyyy", { locale: sv })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Kund</span>
                  <p className="font-medium">{selectedOrder.client?.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Beställningsdatum</span>
                  <p className="font-medium">
                    {format(new Date(selectedOrder.orderDate!), "d MMMM yyyy, HH:mm", { locale: sv })}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Totalt</span>
                  <p className="font-medium">{selectedOrder.totalAmount} kr</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produkt</TableHead>
                    <TableHead className="text-right">Antal</TableHead>
                    <TableHead className="text-right">Pris</TableHead>
                    <TableHead className="text-right">Summa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedOrder.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.product?.name || "Okänd produkt"}</TableCell>
                      <TableCell className="text-right">
                        {item.quantity} {getUnitLabel(item.product?.sellUnit || "pcs")}
                      </TableCell>
                      <TableCell className="text-right">{item.unitPrice} kr</TableCell>
                      <TableCell className="text-right">{item.totalPrice} kr</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {selectedOrder.notes && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Meddelande från kund
                  </h4>
                  <p className="text-sm text-muted-foreground italic" data-testid="text-order-notes">{selectedOrder.notes}</p>
                </div>
              )}
              {selectedOrder.client && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Kundinformation</h4>
                  <div className="text-sm space-y-1 text-muted-foreground">
                    {selectedOrder.client.address && <p>{selectedOrder.client.address}</p>}
                    {selectedOrder.client.phone && <p>Tel: {selectedOrder.client.phone}</p>}
                    {selectedOrder.client.email && <p>E-post: {selectedOrder.client.email}</p>}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" asChild>
                  <Link href={`/admin/delivery-note/${selectedOrder.id}`}>
                    Skriv ut leveranssedel
                  </Link>
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        .print-delivery-notes {
          display: none;
        }

        .a4-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }

        @media print {
          @page {
            size: A4;
            margin: 0;
          }

          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          body * {
            visibility: hidden;
          }

          .print-delivery-notes.print-active {
            display: block !important;
            visibility: visible;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          .print-delivery-notes.print-active * {
            visibility: visible;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .delivery-note-page {
            width: 210mm;
            min-height: 297mm;
            padding: 10mm;
            box-sizing: border-box;
            background: white !important;
            color: black !important;
            page-break-after: always;
            break-after: page;
            overflow: visible !important;
            display: block !important;
          }

          .delivery-note-page:last-child {
            page-break-after: avoid;
            break-after: avoid;
          }
        }
      `}</style>

      <AddOrderDialog open={addOrderOpen} onOpenChange={setAddOrderOpen} showStatusAndDate />
    </div>
  );
}
